import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { Skeleton } from 'primereact/skeleton';
import { AuthenticatedLink } from '../../components/AuthenticateLink';
import { showErrorToast, showSuccessToast } from '../../utils/ToastUtils';
import { errorService } from '../../services/ErrorService';
import moment from 'moment';

import { 
    selectLoading, 
    selectImportationList, 
    selectImportationListCount, 
    getImportationsAsync, 
    getImportationsCountAsync, 
    downloadImportationFileAsync,
    importationStarted,
    importationFinished,
} from './importationSlice';

const ImportationsView = () => {

    const defaultGridParameters = {page: 0, first: 0, rows: 10, sortField: 'id', sortOrder: 1, filters: {}}
    
    const [selectedImportations, setSelectedImportations] = useState(null);

    const [gridParameters, setGridParameters] = useState(defaultGridParameters);

    const [expandedRows, setExpandedRows] = useState(null);
    const toast = useRef(null);
    const dt = useRef(null);

    const dispatch = useDispatch();
    const loading = useSelector(selectLoading);
    const importationList = useSelector(selectImportationList);
    const importationListCount = useSelector(selectImportationListCount);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                await errorService.retryOperation(async () => {
                    await Promise.all([
                        dispatch(getImportationsAsync(defaultGridParameters)).unwrap(),
                        dispatch(getImportationsCountAsync(defaultGridParameters)).unwrap()
                    ]);
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao carregar importações', error);
            }
        };

        loadInitialData();
    }, [dispatch]);

    const loadData = async () => {
        try {
            await errorService.retryOperation(async () => {
                await Promise.all([
                    dispatch(getImportationsAsync(gridParameters)).unwrap(),
                    dispatch(getImportationsCountAsync(gridParameters)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao carregar dados', error);
        }
    };

    const initialLoadDataGrid = () => {
        loadData();
    }

    const loadDataGrid = () => {
        loadData();
    }

    const loadDataGridKeepingParameters = () => {
        loadData();
    }
    
    const onSort = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const sortParams = {
                    ...gridParameters,
                    sortField: event.sortField,
                    sortOrder: event.sortOrder,
                    filters: {...gridParameters.filters}
                };
                setGridParameters(sortParams);
                await Promise.all([
                    dispatch(getImportationsAsync(sortParams)).unwrap(),
                    dispatch(getImportationsCountAsync(sortParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao ordenar importações', error);
        }
    };

    const onFilter = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const filterParams = {
                    ...gridParameters,
                    page: 0,
                    first: 0,
                    filters: event.filters
                };
                setGridParameters(filterParams);
                await Promise.all([
                    dispatch(getImportationsAsync(filterParams)).unwrap(),
                    dispatch(getImportationsCountAsync(filterParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao filtrar importações', error);
        }
    };
    
    const onPage = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const pageParams = {
                    ...event,
                    filters: {...gridParameters.filters}
                };
                setGridParameters(pageParams);
                await dispatch(getImportationsAsync(pageParams)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao mudar página', error);
        }
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    }

    const rightToolbarTemplate = () => {
        return (
            <React.Fragment>
                {/* <Button label="Excluir" icon="pi pi-trash" className="mr-2 p-button-danger" onClick={confirmDeleteSelected} disabled={!selectedImportations || !selectedImportations.length} /> */}
                <FileUpload mode="basic" accept="xlsx/*" maxFileSize={1000000} label="Importar" chooseLabel="Importar" className="mr-2 inline-block" 
                uploadHandler={(e) => console.log('uploadHandler', e)} 
                onBeforeSend={(e) => {
                    e.xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem(process.env.REACT_APP_TOKEN_KEY)}`);
                    e.xhr.setRequestHeader('x-client-id', `${process.env.REACT_APP_API_X_CLIENT_ID}`);
                    console.log('onBeforeSend', e)
                    dispatch(importationStarted())
                }} 
                onBeforeUpload={(e) => {
                    toast.current.show({ severity: 'info', summary: 'Informação', detail: 'Arquivo enviado', life: 3000 });
                }}
                onUpload={(e) => {
                    console.log('onUpload', e)
                    toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Arquivo de importação processado', life: 5000 });
                    dispatch(importationFinished({gridParameters}))
                }} 
                onError={(e) => {
                    console.log('onError', e)
                    toast.current.show({ severity: 'error', summary: 'Erro', detail: 'Falha na importação', life: 3000 });
                    dispatch(importationFinished({gridParameters}))
                }}
                onProgress={(e) => {
                    console.log('onProgress', e)
                    toast.current.show({ severity: 'info', summary: 'Informação', detail: 'Importação em progresso', life: 5000 });
                }}
                url={`${process.env.REACT_APP_API_URL}/importations/files`}
                />
                {/* <Button label="Exportar" icon="pi pi-upload" className="p-button-help" onClick={exportCSV} /> */}
            </React.Fragment>
        )
    }

    const createdAtBodyTemplate = (rowData) => {
        if (!rowData.createdAt) return null;
        return (
            <>
                <span className="p-column-title">Data</span>
                {moment(rowData.createdAt).locale('pt-br').format('DD/MM/YYYY HH:mm')}
            </>
        );
    }

    const fileNameBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Arquivo</span>
                {rowData.fileName}
            </>
        );
    }

    const testBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Prova</span>
                {rowData.test.title}
            </>
        );
    }

    const fileSizeTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Tamanho</span>
                {rowData.fileSize}
            </>
        );
    }

    const hasErrorsBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Erros?</span>
                {rowData.hasErrors ? 'Sim' : 'Não'}
            </>
        );
    }

    const rowExpansionTemplate = (data) => {
      return (
          <div className="orders-subtable">
              <h5>Erros:</h5>
              <DataTable value={data.errorList} responsiveLayout="scroll" emptyMessage='Nenhum erro identificado.'>
                <Column field="description" header="Descrição" sortable></Column>
              </DataTable>
          </div>
      );
    }

    const actionBodyTemplate = (rowData) => {
        return (
            <AuthenticatedLink url={`${process.env.REACT_APP_API_URL}/importations/download/${rowData.id}`} filename={rowData.fileName} >
                <Button icon="pi pi-download" tooltip='Download XLSX' className="p-button-rounded p-button-primary mr-2"/>
            </AuthenticatedLink>
        );
    }

    return (
      <>
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">Gestão de importações</h5>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={loadDataGrid}
                            disabled={loading}
                        />
                    </div>
                    <DataTable ref={dt} value={loading ? Array.from({ length: gridParameters.rows }, (_, i) => ({ id: `skeleton-${i}` })) : importationList} selection={selectedImportations} onSelectionChange={(e) => setSelectedImportations(e.value)}
                        header={<div className="flex justify-content-start">{rightToolbarTemplate()}</div>}
                        dataKey="id"
                        paginator
                        sortField={gridParameters.sortField}
                        sortOrder={gridParameters.sortOrder}
                        rows={gridParameters.rows}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Exibindo de {first} à {last} de {totalRecords} registros"
                        emptyMessage="Sem resultados."
                        responsiveLayout="scroll"
                        rowExpansionTemplate={rowExpansionTemplate}
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        totalRecords={importationListCount}
                        lazy={true}
                        first={gridParameters.first}
                        onSort={(e) => onSort(e)}
                        onFilter={(e) => onFilter(e)}
                        onPage={(e) => onPage(e)}
                        >
                        <Column expander style={{ width: '1em' }} body={loading ? () => <Skeleton /> : undefined} />
                        <Column field="createdAt" header="Data" sortable={!loading} body={loading ? () => <Skeleton /> : createdAtBodyTemplate} headerStyle={{ width: '1em', minWidth: '5rem' }}></Column>
                        <Column field="fileName" header="Arquivo" sortable={!loading} body={loading ? () => <Skeleton /> : fileNameBodyTemplate} headerStyle={{ width: '1em', minWidth: '5rem' }}></Column>
                        <Column field="hasErrors" header="Erros?" body={loading ? () => <Skeleton /> : hasErrorsBodyTemplate} headerStyle={{ width: '1em', minWidth: '5rem' }}></Column>
                        <Column body={loading ? () => <Skeleton /> : actionBodyTemplate} style={{ width: '15%', minWidth: '10rem' }}></Column>
                    </DataTable>
                </div>
            </div>
        </div>
      </>
    );
}

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(ImportationsView, comparisonFn);